import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { pipelineApi, PipelineItem } from '../services/api';
import Swal from 'sweetalert2';
import styles from './Pipeline.module.css';

type Stage = PipelineItem['stage'];

const STAGES: { key: Stage; label: string; color: string }[] = [
  { key: 'idea', label: 'Ideas', color: '#6b7280' },
  { key: 'proposal', label: 'Proposal', color: '#3b82f6' },
  { key: 'active', label: 'Active', color: '#f59e0b' },
  { key: 'complete', label: 'Complete', color: '#22c55e' },
];

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getDaysRemaining(deadline: string): number {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diff = deadlineDate.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function Pipeline() {
  const { getIdToken } = useAuth();
  const [items, setItems] = useState<PipelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await pipelineApi.list(token);
      setItems(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }

  async function handleMove(item: PipelineItem, newStage: Stage) {
    try {
      const token = await getIdToken();
      if (!token) return;

      const response = await pipelineApi.move(token, item.id, newStage);
      setItems(prev => prev.map(i => i.id === item.id ? response.item : i));
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to move item',
      });
    }
  }

  async function handleAddDeal() {
    const result = await Swal.fire({
      title: 'Add New Deal',
      html: `
        <input id="swal-title" class="swal2-input" placeholder="Deal title *">
        <input id="swal-partner" class="swal2-input" placeholder="Partner organization">
        <input id="swal-value" class="swal2-input" type="number" placeholder="Estimated value ($)">
        <select id="swal-stage" class="swal2-select">
          <option value="idea">Idea</option>
          <option value="proposal">Proposal</option>
          <option value="active">Active</option>
        </select>
        <textarea id="swal-desc" class="swal2-textarea" placeholder="Description"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: 'Create',
      preConfirm: () => {
        const title = (document.getElementById('swal-title') as HTMLInputElement).value.trim();
        if (!title) {
          Swal.showValidationMessage('Title is required');
          return null;
        }
        const partner = (document.getElementById('swal-partner') as HTMLInputElement).value.trim();
        const valueStr = (document.getElementById('swal-value') as HTMLInputElement).value;
        const stage = (document.getElementById('swal-stage') as HTMLSelectElement).value as Stage;
        const desc = (document.getElementById('swal-desc') as HTMLTextAreaElement).value.trim();

        return {
          title,
          partnerOrg: partner || null,
          estimatedValue: valueStr ? parseInt(valueStr, 10) * 100 : null,
          stage,
          description: desc || null,
        };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await pipelineApi.create(token, result.value);
        setItems(prev => [...prev, response.item]);
        Swal.fire({ icon: 'success', title: 'Deal created!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to create deal',
        });
      }
    }
  }

  async function handleEdit(item: PipelineItem) {
    const result = await Swal.fire({
      title: 'Edit Deal',
      html: `
        <input id="swal-title" class="swal2-input" value="${item.title}" placeholder="Deal title *">
        <input id="swal-partner" class="swal2-input" value="${item.partnerOrg || ''}" placeholder="Partner organization">
        <input id="swal-contact" class="swal2-input" value="${item.partnerContact || ''}" placeholder="Contact name">
        <input id="swal-email" class="swal2-input" value="${item.partnerEmail || ''}" placeholder="Contact email">
        <input id="swal-value" class="swal2-input" type="number" value="${item.estimatedValue ? item.estimatedValue / 100 : ''}" placeholder="Estimated value ($)">
        <select id="swal-valuetype" class="swal2-select">
          <option value="tbd" ${item.valueType === 'tbd' ? 'selected' : ''}>TBD</option>
          <option value="one_time" ${item.valueType === 'one_time' ? 'selected' : ''}>One-time</option>
          <option value="recurring" ${item.valueType === 'recurring' ? 'selected' : ''}>Recurring</option>
        </select>
        <input id="swal-deadline" class="swal2-input" type="date" value="${item.deadline ? item.deadline.split('T')[0] : ''}" placeholder="Deadline">
        <input id="swal-proposal" class="swal2-input" value="${item.proposalUrl || ''}" placeholder="Proposal URL">
        <textarea id="swal-desc" class="swal2-textarea" placeholder="Description">${item.description || ''}</textarea>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save',
      denyButtonText: 'Delete',
      denyButtonColor: '#dc2626',
      preConfirm: () => {
        const title = (document.getElementById('swal-title') as HTMLInputElement).value.trim();
        if (!title) {
          Swal.showValidationMessage('Title is required');
          return null;
        }
        return {
          title,
          partnerOrg: (document.getElementById('swal-partner') as HTMLInputElement).value.trim() || null,
          partnerContact: (document.getElementById('swal-contact') as HTMLInputElement).value.trim() || null,
          partnerEmail: (document.getElementById('swal-email') as HTMLInputElement).value.trim() || null,
          estimatedValue: (document.getElementById('swal-value') as HTMLInputElement).value
            ? parseInt((document.getElementById('swal-value') as HTMLInputElement).value, 10) * 100
            : null,
          valueType: (document.getElementById('swal-valuetype') as HTMLSelectElement).value,
          deadline: (document.getElementById('swal-deadline') as HTMLInputElement).value || null,
          proposalUrl: (document.getElementById('swal-proposal') as HTMLInputElement).value.trim() || null,
          description: (document.getElementById('swal-desc') as HTMLTextAreaElement).value.trim() || null,
        };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await pipelineApi.update(token, item.id, result.value);
        setItems(prev => prev.map(i => i.id === item.id ? response.item : i));
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to update deal',
        });
      }
    } else if (result.isDenied) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Delete this deal?',
        text: item.title,
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc2626',
      });

      if (confirm.isConfirmed) {
        try {
          const token = await getIdToken();
          if (!token) return;

          await pipelineApi.delete(token, item.id);
          setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: err instanceof Error ? err.message : 'Failed to delete deal',
          });
        }
      }
    }
  }

  async function handleAddNote(item: PipelineItem) {
    const result = await Swal.fire({
      title: 'Add Note',
      input: 'textarea',
      inputPlaceholder: 'Enter your note...',
      showCancelButton: true,
      confirmButtonText: 'Add',
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await pipelineApi.addNote(token, item.id, result.value);
        setItems(prev => prev.map(i => i.id === item.id ? response.item : i));
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to add note',
        });
      }
    }
  }

  function getItemsByStage(stage: Stage): PipelineItem[] {
    return items.filter(i => i.stage === stage);
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading pipeline...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
        <button onClick={loadData} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Pipeline</h1>
        <button className={styles.addBtn} onClick={handleAddDeal}>
          + Add Deal
        </button>
      </header>

      <div className={styles.board}>
        {STAGES.map(({ key, label, color }) => (
          <div key={key} className={styles.column}>
            <div className={styles.columnHeader} style={{ borderColor: color }}>
              <span className={styles.columnTitle}>{label}</span>
              <span className={styles.columnCount}>{getItemsByStage(key).length}</span>
            </div>
            <div className={styles.columnCards}>
              {getItemsByStage(key).map(item => {
                const isExpanded = expandedId === item.id;
                const daysRemaining = item.deadline ? getDaysRemaining(item.deadline) : null;
                const isUrgent = daysRemaining !== null && daysRemaining < 7 && daysRemaining >= 0;
                const isOverdue = daysRemaining !== null && daysRemaining < 0;

                return (
                  <div
                    key={item.id}
                    className={`${styles.card} ${isUrgent ? styles.urgent : ''} ${isOverdue ? styles.overdue : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                  >
                    <div className={styles.cardHeader}>
                      <h3 className={styles.cardTitle}>{item.title}</h3>
                      <button
                        className={styles.editBtn}
                        onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                      >
                        Edit
                      </button>
                    </div>

                    {item.partnerOrg && (
                      <div className={styles.partner}>{item.partnerOrg}</div>
                    )}

                    {item.estimatedValue && (
                      <div className={styles.value}>
                        {formatCurrency(item.estimatedValue)}
                        {item.valueType !== 'tbd' && (
                          <span className={styles.valueType}>
                            {item.valueType === 'recurring' ? '/mo' : ' one-time'}
                          </span>
                        )}
                      </div>
                    )}

                    {item.deadline && key === 'active' && (
                      <div className={`${styles.deadline} ${isUrgent ? styles.deadlineUrgent : ''} ${isOverdue ? styles.deadlineOverdue : ''}`}>
                        {isOverdue
                          ? `${Math.abs(daysRemaining!)} days overdue`
                          : daysRemaining === 0
                            ? 'Due today'
                            : `${daysRemaining} days left`}
                      </div>
                    )}

                    {isExpanded && (
                      <div className={styles.expanded}>
                        {item.description && (
                          <p className={styles.description}>{item.description}</p>
                        )}

                        {item.partnerContact && (
                          <div className={styles.contact}>
                            Contact: {item.partnerContact}
                            {item.partnerEmail && ` (${item.partnerEmail})`}
                          </div>
                        )}

                        {item.proposalUrl && (
                          <a
                            href={item.proposalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.link}
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Proposal
                          </a>
                        )}

                        {item.notes.length > 0 && (
                          <div className={styles.notes}>
                            <strong>Notes:</strong>
                            {item.notes.slice(-3).map((note, idx) => (
                              <div key={idx} className={styles.note}>
                                <span className={styles.noteDate}>
                                  {new Date(note.date).toLocaleDateString()}
                                </span>
                                {note.content}
                              </div>
                            ))}
                          </div>
                        )}

                        <button
                          className={styles.noteBtn}
                          onClick={(e) => { e.stopPropagation(); handleAddNote(item); }}
                        >
                          + Add Note
                        </button>

                        <div className={styles.moveButtons}>
                          {STAGES.filter(s => s.key !== key).map(s => (
                            <button
                              key={s.key}
                              className={styles.moveBtn}
                              onClick={(e) => { e.stopPropagation(); handleMove(item, s.key); }}
                            >
                              → {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {getItemsByStage(key).length === 0 && (
                <div className={styles.emptyColumn}>No items</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
